import React, {FC} from "react";
import {Drawer, Space} from "antd";
import {FormikProps} from "formik";
import moment from "moment";
import {
    EditableDate,
    EditableStartDateTime,
    EditableEndDateTime,
    EditableLocation,
    EditableInstructor,
} from "./CourseViews/CourseLessonViews";
import {PossibleFormikPropsType} from "./CourseCreate/CourseCreateForm";
import i18n from "../../services/i18n";

type CourseLessonEditDrawerProps = {
    open: boolean;
    onClose: () => void;
    index: number;
    formikProps: FormikProps<PossibleFormikPropsType>;
    disabled?: boolean;
};

const labelStyle: React.CSSProperties = {
    fontWeight: 500,
    marginBottom: 4,
    display: "block",
};

/**
 * Drawer-Komponente zum Bearbeiten einer einzelnen Kurseinheit.
 * Wird in der Compact View (<1200px) anstelle der inline-editierbaren Tabellenzellen verwendet.
 * Nutzt die gleichen Editable-Komponenten wie die Desktop-Tabelle.
 */
const CourseLessonEditDrawer: FC<CourseLessonEditDrawerProps> = ({
    open,
    onClose,
    index,
    formikProps,
    disabled = false,
}) => {
    const lesson = formikProps.values.lessons[index];
    if (!lesson) {
        return null;
    }

    const lessonNumber = index + 1;
    const dateText = moment(lesson.startDateTime).format("dd DD.MM.YY");

    return (
        <Drawer
            title={`Kurseinheit ${lessonNumber} - ${dateText}`}
            placement="right"
            onClose={onClose}
            open={open}
            width={400}
        >
            <Space direction="vertical" size="large" style={{width: "100%"}}>
                <div>
                    <label style={labelStyle}>
                        {i18n.containers.courses.CourseCreate.courseLessons.date.label()}
                    </label>
                    <EditableDate
                        date={lesson.startDateTime}
                        index={index}
                        formikProps={formikProps}
                        testId="DrawerEditableDate"
                        placeholder={i18n.containers.courses.CourseCreate.firstCourseLessonDate.placeholder()}
                        style={{width: "100%"}}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        {i18n.containers.courses.CourseCreate.courseLessons.startTime.label()}
                    </label>
                    <EditableStartDateTime
                        startTime={lesson.startDateTime}
                        index={index}
                        formikProps={formikProps}
                        testId="DrawerEditableStartDateTime"
                        style={{width: "100%"}}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        {i18n.containers.courses.CourseCreate.courseLessons.endTime.label()}
                    </label>
                    <EditableEndDateTime
                        endTime={lesson.endDateTime}
                        index={index}
                        formikProps={formikProps}
                        testId="DrawerEditableEndDateTime"
                        style={{width: "100%"}}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        {i18n.containers.courses.CourseCreate.courseLessons.venue.label()}
                    </label>
                    <EditableLocation
                        formikProps={formikProps}
                        index={index}
                        testId="DrawerEditableLocation"
                        style={{width: "100%"}}
                        disabled={disabled}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        {i18n.containers.courses.CourseCreate.courseLessons.instructor.label()}
                    </label>
                    <EditableInstructor
                        maxTagCount={3}
                        selectedInstructors={lesson.instructors ?? []}
                        index={index}
                        formikProps={formikProps}
                        testId="DrawerEditableInstructor"
                        style={{width: "100%"}}
                        disabled={disabled}
                    />
                </div>
            </Space>
        </Drawer>
    );
};

export default CourseLessonEditDrawer;
